import { useAuth } from "contexts/authContext";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import { intervalToDuration, isPast, parseISO } from "date-fns";
import { socket } from "hooks/useSocket";
import { IOccurrence } from "interfaces/Occurrence";
import { IGetByReport, IReport } from "interfaces/Report";
import { useEffect, useState } from "react";
import { getOccurrenceExpireToken } from "services/OccurrenceService";
import { fireToast } from "services/sweetAlertConfig";
import { copyToClipboard } from "utils/copyToClipboard";

export type IdleStatus = "onGoing" | "expanded" | "stopped" | "removed" | undefined;

export type IdleOccurrenceUpdateProps = {
  occurrence: IOccurrence;
  status: IdleStatus;
};

type UseMonitoringCardParams = {
  occurrence: IOccurrence;
  priorizedCard: (isFavorite: boolean, occurrenceId: string) => void;
  isFavorite?: boolean;
};

export type TimeRemaining = {
  hours: number;
  minutes: number;
  seconds: number;
};

export type CountdownResponse = {
  date: Date;
  timeRemaining: TimeRemaining;
};

export function useMonitoringCard({ isFavorite, occurrence, priorizedCard }: UseMonitoringCardParams) {
  const [isLate, setIsLate] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isCancelled, setIsCancelled] = useState<boolean>(false);
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [isFree, setIsFree] = useState<boolean>(false);
  const [notPr, setNotPr] = useState<boolean>(false);
  const [
    isEditOccurrenceManagementModalOpen,
    setIsEditOccurrenceManagementModalOpen,
  ] = useState<boolean>(false);

  const [idleStatus, setIdleStatus] = useState<IdleStatus>();

  const [isCountdownLoading, setIsCountdownLoading] = useState<boolean>(true);

  const [timeRemaining, setTimeRemaining] = useState<string>("--");

  const [isPriorized, setIsPriorized] = useState<boolean>(isFavorite || false);
  const [occurrenceTotalTime, setOccurrenceTotalTime] = useState<string>();
  const [occurrenceElapsedTime, setOccurrenceElapsedTime] = useState<number>(0);
  const [occurrenceChanges, setOccurrenceChanges] = useState<
    IOccurrence | undefined
  >();

  const { occurrenceType, setOccurrenceToEditId } = useManualOccurrence();
  const { tenant } = useAuth();


  useEffect(() => {
    socket.on(`occurrence-change-${occurrence.id}`, onOccurrenceChange);

    const idleOccurrenceSocket = `idle-occurrence-${occurrence.id}`;

    if (occurrence.status === "aguardando") {
      socket.emit("@idle-occurrence/start", occurrence);

      socket.on(idleOccurrenceSocket, handleIdleOccurrenceUpdate);

      setIsCountdownLoading(false);
      return setIsFree(true);
    } else if (occurrence.status === 'aguardando_cancelamento') {
      setIsCountdownLoading(false);
      return setIsCancelled(true);
    }

    const report = occurrence.report?.length ? occurrence.report[0] : undefined;
    if (!report) {
      setIsCountdownLoading(false);
      return setIsFree(true);
    }

    treatReportBadge(report);
    setIsCountdownLoading(false);

    const countdownSocket = `countdown-${report.id}`;

    const countdownStopSocket = `countdown-stop-${report.id}`;

    const _innerCountdownStop = `stop-countdown-${report.id}`;

    socket.on(countdownSocket, getOccurrenceCountdown);

    socket.on(_innerCountdownStop, stopOccurrenceCountdown);

    socket.on(`report-update-${report.id}`, updateReportBadge);

    return () => {
      socket.off(countdownSocket);
      socket.off(countdownStopSocket);
      socket.off(idleOccurrenceSocket);
    };
  }, [occurrence]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    socket.on("stop-countdown", (string: string) => {
      socket.emit("stop-countdown", string);
    });
  }, []);


  async function copyShareLink(occurrence: IOccurrence) {
    const occurrenceId = occurrence.id;
    const shareToken = await getOccurrenceExpireToken(occurrenceId);
    const shareUrl = `https://preser.app/share/${shareToken}`;

    navigator.clipboard.writeText(shareUrl);

    fireToast("Link de compartilhamento copiado com sucesso!", 3000, "top-end");

    fireToast(
      "ATENÇÃO! O link gerado será valido por apenas 6 horas.",
      3000,
      "top-end"
    );
  }

  async function fetchDurationTime() {
    const start = new Date(occurrence?.createdAt);

    if (start) {
      const interval = intervalToDuration({
        end: new Date(),
        start: new Date(start),
      });

      const time = `${interval.hours}h ${interval.minutes}min`;

      const intervalInSeconds =
        interval.days! * 86500 +
        interval.hours! * 3600 +
        interval.minutes! * 60 +
        interval.seconds!;

      setOccurrenceElapsedTime(intervalInSeconds);

      const minutesTotal = interval.hours! + interval.minutes!;

      if (
        minutesTotal >= 15 &&
        (occurrence.status !== "andamento" || idleStatus === "stopped")
      ) {
        setNotPr(true);
      }

      setOccurrenceTotalTime(time);
    }
  }

  function handleIdleOccurrenceUpdate(
    idleOccurrence: IdleOccurrenceUpdateProps
  ) {
    if (idleOccurrence.status === "removed") {
      return setIdleStatus(undefined);
    }

    setIdleStatus(idleOccurrence.status);
  }

  useEffect(() => {
    fetchDurationTime();
  }, [occurrence]); // eslint-disable-line react-hooks/exhaustive-deps

  function getTotalTime(report: IGetByReport) {
    for (const attachment of report.attachments) {
      if (attachment.category === "starter_km") return attachment.createdAt;
    }
  }

  function getCurrentIdleStatus() {
    switch (idleStatus) {
      case "onGoing":
        return "Exibindo para pr's em um raio de 15km...";
      case "expanded":
        return "Expandindo exibição para um raio de 30km...";
      case "stopped":
        return "Nenhum pronta-resposta aceitou em um periodo de 4 minutos. Mantendo exibição em um raio de 30km.";
      default:
        return "";
    }
  }

  function getAddress(street: string | undefined, number: string | undefined) {
    if (!number) return street;
    return `${street}, ${number}`;
  }

  function handleIsPriorized<T>(occurrence: string) {
    const pinnedCards =
      JSON.parse(localStorage.getItem("pinned") as string) ?? [];

    if (!pinnedCards.map(({ id }: { id: string }) => id).includes(occurrence)) {
      const updatedPinnedCards: T[] = [...pinnedCards, { id: occurrence }];
      localStorage.setItem("pinned", JSON.stringify(updatedPinnedCards));
    }

    if (pinnedCards.map(({ id }: { id: string }) => id).includes(occurrence)) {
      const updatedPinnedCards: T[] = pinnedCards.filter(
        ({ id }: { id: string }) => id !== occurrence
      );
      localStorage.setItem("pinned", JSON.stringify(updatedPinnedCards));
    }

    setIsPriorized(!isPriorized);
    priorizedCard(!isPriorized, occurrence);
  }

  function updateReportBadge(report: IReport) {
    treatReportBadge(report);
  }

  function resetBadges() {
    setIsLate(false);
    setIsCompleted(false);
    setIsWaiting(false);
    setIsFree(false);
  }

  function formatProtocol(uuid: string) {
    const splittedUuid = uuid.split("-");
    if (splittedUuid.length) return splittedUuid[0];
    return "--";
  }

  function onOccurrenceChange(socketOccurrence: IOccurrence) {
    setOccurrenceChanges(socketOccurrence);
  }

  function treatReportBadge(report: IReport) {
    resetBadges();

    if (occurrence.status === 'cancelado' || occurrence.status === 'aguardando_cancelamento') {
      return setIsCancelled(true);
    }

    if (!report.estimatedArrival && !report.arrivalTime) {
      return setIsWaiting(true);
    }

    if (report.estimatedArrival && report.arrivalTime) {
      return setIsCompleted(true);
    }

    if (
      report.estimatedArrival &&
      isPast(parseISO(String(report.estimatedArrival)))
    ) {
      setTimeRemaining("O pronta resposta não chegou");
      return setIsLate(true);
    }

    if (report.estimatedArrival && !report.arrivalTime) {
      return socket.emit("policemanArrival", {
        tenantId: tenant?.id,
        report
      });
    }
  }

  function isActualPictureRejected() {
    const _report = occurrence.report;

    try {
      return Boolean(_report && _report[0].description);
    } catch {
      return false;
    }
  }

  function getWorkType() {
    return isPolicemanWorktype() ? "pronta-resposta" : "assistente";
  }

  function isPolicemanWorktype() {
    return occurrence.workType === "Policial";
  }

  function formatTimeRemaining(timeRemaining: TimeRemaining) {
    const hours = timeRemaining.hours;
    const minutes = timeRemaining.minutes;
    const seconds = timeRemaining.seconds;

    if (hours > 0) {
      return `${timeRemaining.hours}h ${timeRemaining.minutes}m`;
    }

    if (hours <= 0 && minutes > 0) {
      return `${timeRemaining.minutes} min.`;
    }

    if (hours <= 0 && minutes <= 0 && seconds > 0) {
      return "< 1 min.";
    }

    if (hours <= 0 && minutes <= 0 && seconds <= 0) {
      socket.off(`countdown-${occurrence.id}`);

      setIsLate(true);

      return "O pronta resposta não chegou";
    }

    return "";
  }

  function getOccurrenceCountdown(response: CountdownResponse) {
    const timeRemaining = response.timeRemaining;
    const message = formatTimeRemaining(timeRemaining);
    setTimeRemaining(message);
  }

  async function handleOccurrenceEdit() {
    setOccurrenceToEditId(occurrence.id);
    setIsEditOccurrenceManagementModalOpen(true);
  }

  function stopOccurrenceCountdown(reportId: string) {
    socket.emit(`countdown-stop-${reportId}`, reportId);
    setIsCompleted(true);
    setTimeRemaining("--");
  }

  function isWaitingApproval() {
    const status = occurrence.status;
    return (
      status === "aprovacao" ||
      status === "aprovacao-km-final" ||
      status === "aprovacao-km-inicial"
    );
  }

  function getApprovalMessage() {
    const prefixString = `O ${getWorkType()} está aguardando aprovação`;
    const recusedString = `A foto do <span>hodômetro [STATUS]</span> enviada anteriormente foi negada.<br/>Aguardando novo envio do ${getWorkType()}`;

    switch (occurrence.status) {
      case "aprovacao":
        return prefixString + "<br/>das fotos da ocorrência";
      case "aprovacao-km-inicial":
        if (isActualPictureRejected()) {
          return recusedString.replace("[STATUS]", "inicial");
        }

        return (
          prefixString + "<br/>da foto de seu <span>hodômetro inicial</span>"
        );
      case "aprovacao-km-final":
        if (isActualPictureRejected()) {
          return recusedString.replace("[STATUS]", "final");
        }

        return (
          prefixString + "<br/>da foto de seu <span>hodômetro final</span>"
        );
    }
  }

  function copyProtocol() {
    const protocol = occurrence.protocol
      ? String(occurrence.protocol)
      : formatProtocol(occurrence.id);

    copyToClipboard(protocol);
    fireToast(`Protocolo ${protocol} copiado com sucesso`);
  }

  function getBadgeColor() {
    const status = occurrence.status;

    if (isWaitingApproval()) {
      return "aguardando";
    }

    if (status === "andamento") return "andamento";

    if (status === "aguardando_cancelamento") return "aguardando_cancelamento";

    return "finalizado";
  }

  return {
    isPriorized,
    isLate,
    notPr,
    isCompleted,
    isCountdownLoading,
    timeRemaining,
    isFree,
    isWaiting,
    isCancelled,
    occurrenceElapsedTime,
    idleStatus,
    isEditOccurrenceManagementModalOpen,
    occurrenceType,
    handleOccurrenceEdit,
    handleIsPriorized,
    getBadgeColor,
    getWorkType,
    isWaitingApproval,
    getApprovalMessage,
    isActualPictureRejected,
    formatProtocol,
    copyProtocol,
    getAddress,
    getCurrentIdleStatus,
    setIsEditOccurrenceManagementModalOpen,
    copyShareLink
  }
}